/**
 * AcceptSharedListInvitePage
 *
 * Public page at /invite/:token. Shows a preview of the shared list invite
 * (list name, inviter, role). If the user is not logged in, redirects to /login
 * with a return URL. If logged in, shows an "Accept invite" button.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, Users } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getSharedListInvitePreview, acceptSharedListInvite } from '../api/shared-lists';
import type { SharedListInvitePreview } from '../api/shared-lists';

export function AcceptSharedListInvitePage() {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuth();

  const [preview, setPreview] = useState<SharedListInvitePreview | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [acceptError, setAcceptError] = useState<string | null>(null);

  // Fetch invite preview on mount — this endpoint is public
  useEffect(() => {
    if (!token) return;
    setIsFetching(true);
    getSharedListInvitePreview(token)
      .then((data) => {
        setPreview(data);
        setFetchError(null);
      })
      .catch((err: Error) => {
        setFetchError(err.message || 'This invite link is invalid or has expired.');
      })
      .finally(() => setIsFetching(false));
  }, [token]);

  // Once auth state is known, redirect to login if not authenticated
  useEffect(() => {
    if (authLoading || isFetching) return;
    if (!isAuthenticated) {
      const returnUrl = encodeURIComponent(window.location.pathname);
      navigate(`/login?returnTo=${returnUrl}`, { replace: true });
    }
  }, [authLoading, isFetching, isAuthenticated, navigate]);

  const handleAccept = async () => {
    if (!token) return;
    setIsAccepting(true);
    setAcceptError(null);
    try {
      await acceptSharedListInvite(token);
      // Navigate to the list if we have a list ID; otherwise fall back to index
      if (preview) {
        // The preview doesn't return listId directly, but the accept endpoint
        // returns { success: true }. We navigate to shared-lists index and let
        // the user click through. The ID isn't in the preview shape per the spec.
        navigate('/app/shared-lists', { replace: true });
      } else {
        navigate('/app', { replace: true });
      }
    } catch (err) {
      setAcceptError(err instanceof Error ? err.message : 'Failed to accept invite.');
      setIsAccepting(false);
    }
  };

  // Show spinner while auth or preview is loading
  if (authLoading || isFetching) {
    return (
      <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600 dark:text-primary-400" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center p-4">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mx-auto">
            <Users className="w-6 h-6 text-red-500" />
          </div>
          <h1 className="text-xl font-semibold text-text-primary-light dark:text-text-primary-dark">
            Invite not found
          </h1>
          <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
            {fetchError}
          </p>
          <a
            href="/app"
            className="inline-block mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
          >
            Go to Danny
          </a>
        </div>
      </div>
    );
  }

  // If not authenticated, the useEffect above will redirect — show nothing
  if (!isAuthenticated || !preview) return null;

  const roleLabel = preview.role.charAt(0).toUpperCase() + preview.role.slice(1);

  return (
    <div className="min-h-screen bg-surface-base-light dark:bg-surface-base-dark flex items-center justify-center p-4">
      <div className="max-w-sm w-full">
        <div className="bg-surface-raised-light dark:bg-surface-raised-dark border border-border-subtle-light dark:border-border-subtle-dark rounded-lg p-6 space-y-5">
          {/* Icon */}
          <div className="w-12 h-12 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto">
            <Users className="w-6 h-6 text-primary-600 dark:text-primary-400" />
          </div>

          {/* Heading */}
          <div className="text-center space-y-1">
            <h1 className="text-lg font-semibold text-text-primary-light dark:text-text-primary-dark">
              You&apos;re invited to join a shared list
            </h1>
            <p className="text-text-secondary-light dark:text-text-secondary-dark text-sm">
              {preview.inviterName} has invited you to{' '}
              <span className="font-medium text-text-primary-light dark:text-text-primary-dark">
                {preview.listName}
              </span>
            </p>
          </div>

          {/* Role badge */}
          <div className="flex items-center justify-center">
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-mono font-medium bg-primary-900/10 dark:bg-primary-900/20 text-primary-700 dark:text-primary-300">
              Role: {roleLabel}
            </span>
          </div>

          {/* Accept error */}
          {acceptError && (
            <p className="text-sm text-red-600 dark:text-red-400 text-center">{acceptError}</p>
          )}

          {/* Accept button */}
          <button
            onClick={() => void handleAccept()}
            disabled={isAccepting}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded transition-colors"
          >
            {isAccepting && <Loader2 className="w-4 h-4 animate-spin" />}
            {isAccepting ? 'Accepting…' : 'Accept invite'}
          </button>

          {/* Decline link */}
          <div className="text-center">
            <button
              onClick={() => navigate('/app')}
              className="text-xs text-text-tertiary-light dark:text-text-tertiary-dark hover:text-text-secondary-light dark:hover:text-text-secondary-dark transition-colors"
            >
              Decline
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
